import { useState } from 'react'

import { commands, installCommand, prerequisites, startSection } from '@/content/site'

import { Reveal } from './shared/Reveal'
import { SectionHeading } from './shared/SectionHeading'

/** Copy-to-clipboard row. Reverts to "Copy" after 1.5s so it can be reused. */
function CommandRow({ cmd, comment }: { cmd: string; comment?: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cmd)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard is unavailable over plain HTTP and in some embeddings —
      // the command is selectable either way, so this fails quietly.
    }
  }

  return (
    <div className="group flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.02] px-3.5 py-3">
      <span className="mt-0.5 flex-shrink-0 font-mono text-[0.7rem] text-aqua" aria-hidden="true">
        $
      </span>
      <div className="min-w-0 flex-1">
        {comment ? (
          <p className="mb-1 font-mono text-[0.55rem] uppercase tracking-wider text-ink-mute">
            {comment}
          </p>
        ) : null}
        <code className="block break-all font-mono text-[0.72rem] leading-relaxed text-ink-dim">
          {cmd}
        </code>
      </div>
      <button
        type="button"
        onClick={copy}
        className="focus-ring flex-shrink-0 rounded-lg px-2 py-1 font-mono text-[0.55rem] uppercase tracking-wider text-ink-mute transition-colors hover:text-ink"
        aria-label={`Copy command: ${cmd}`}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

export function StartSection() {
  return (
    <section id="start" className="relative py-20 md:py-28">
      <div className="shell">
        <SectionHeading
          eyebrow={startSection.eyebrow}
          title={startSection.title}
          intro={startSection.intro}
        />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.15fr_1fr]">
          <Reveal>
            <div className="flex flex-col gap-3">
              <CommandRow cmd={installCommand} comment="Install the CLI" />
              {commands.map((c) => (
                <CommandRow key={c.cmd} cmd={c.cmd} comment={c.comment} />
              ))}
            </div>
          </Reveal>

          <Reveal i={1}>
            <div className="glass glass-rim glass-hi flex h-full flex-col p-6 md:p-7">
              <p className="micro">Before you start</p>

              <ul className="mt-5 space-y-3">
                {prerequisites.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      className="mt-0.5 flex-shrink-0 text-aqua"
                    >
                      <path
                        d="M20 6L9 17l-5-5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="font-paragraph text-[0.85rem] leading-relaxed text-ink-dim">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              <div
                className="my-6 h-px"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, rgb(255 255 255 / 0.12), transparent)',
                }}
                aria-hidden="true"
              />

              <p className="font-paragraph text-[0.82rem] leading-relaxed text-ink-dim">
                Every command runs the full pipeline — context analysis, generation and dual
                validation — then writes the validated asset to your working directory.
              </p>

              <p className="mt-auto pt-6 font-paragraph text-[0.75rem] leading-relaxed text-ink-mute">
                {startSection.rolloutNote}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}
